// src/components/Chatbot.jsx

import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown'; 
import { useVideoPopup } from './VideoPopupContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png'; 
import './Chatbot.css'; 

const Chatbot = ({ districtId, onClose }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // --- UI STATE ---
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 }); 
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  
  const { showPopup } = useVideoPopup();
  const chatWindowRef = useRef(null);
  const navigate = useNavigate();

  // Scroll to bottom
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTo({ top: chatWindowRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // --- DRAG LOGIC ---
  const handleMouseDown = (e) => {
    // Don't drag if expanded or if clicking buttons
    if (isExpanded || e.target.closest('button') || e.target.closest('.header-controls')) return;
    
    setIsDragging(true);
    dragStartRef.current = { 
        x: e.clientX - position.x, 
        y: e.clientY - position.y 
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault(); 
      setPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      });
    };
    
    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // --- API LOGIC ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg = { role: 'user', content: input };
    
    // Create history for API
    const currentHistory = messages.map(m => ({
        role: m.role === 'bot' ? 'assistant' : 'user',
        content: m.content
    }));

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/.netlify/functions/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            message: userMsg.content, 
            districtId,
            history: currentHistory
        })
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      const answerText = data.answer || "I'm sorry, I couldn't generate an answer from the records.";
      // Ensure 'sources' are saved to the message object
      const botMsg = { role: 'bot', content: answerText, sources: data.sources || [] };
      setMessages(prev => [...prev, botMsg]);

    } catch (error) {
      console.error(error);
      setMessages(prev => [...prev, { role: 'bot', content: "I'm having trouble connecting to the BoardWatch archives right now." }]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- RENDER CONTENT (Autolinks Restored) ---
  const renderMessageContent = (msg) => {
    if (!msg || !msg.content) return null;
    
    let processedContent = msg.content;

    // 1. Pre-process Timestamps: [10:00] -> [10:00](#timestamp-10:00)
    const flexibleTimestampRegex = /(\[?\(?\b\d{1,2}:\d{2}(?::\d{2})?\b\)?\]?)/g;
    processedContent = processedContent.replace(flexibleTimestampRegex, (match) => {
        const clean = match.replace(/[[\]()]/g, '').trim();
        return `[${match}](#timestamp-${clean})`;
    });

    // 2. Pre-process Global Dates: ⟪Meeting Date: 2025-02-10⟫ -> [2025-02-10 Meeting](#meeting-date-2025-02-10)
    const dateRegex = /⟪Meeting Date: (\d{4}-\d{2}-\d{2})⟫/g;
    processedContent = processedContent.replace(dateRegex, (match, date) => {
        return `[${date} Meeting](#meeting-date-${date})`;
    });

    return (
      <div className="markdown-content">
        <ReactMarkdown
            components={{
                a: ({ href, children }) => {
                    // CASE A: Timestamp Links (Video Popup)
                    if (href && href.startsWith('#timestamp-')) {
                        const cleanTime = href.replace('#timestamp-', '');
                        
                        // Find matching source
                        const source = msg.sources?.find(s => 
                            s.timestamp && (s.timestamp.startsWith(cleanTime) || cleanTime.startsWith(s.timestamp))
                        ) || msg.sources?.[0];

                        if (source?.video_url) {
                            return (
                                <span 
                                    className="citation-chip timestamp-chip"
                                    role="button"
                                    onClick={(e) => { 
                                        e.preventDefault(); 
                                        e.stopPropagation(); 
                                        showPopup(cleanTime, source.video_url, e.target); 
                                    }}
                                    onMouseEnter={(e) => showPopup(cleanTime, source.video_url, e.target)}
                                >
                                    {children}
                                </span>
                            );
                        }
                        return <span>{children}</span>;
                    }

                    // CASE B: Meeting Date Links (Deep Navigation)
                    if (href && href.startsWith('#meeting-date-')) {
                        const date = href.replace('#meeting-date-', '');
                        return (
                            <span 
                                className="citation-chip meeting-link-chip"
                                role="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`?tab=meetings#meeting-${date}`);
                                }}
                            >
                                {children}
                            </span>
                        );
                    }

                    // CASE C: Standard Links
                    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                },
                p: ({children}) => <div className="chat-paragraph">{children}</div>,
                ul: ({children}) => <ul className="chat-list">{children}</ul>,
                li: ({children}) => <li className="chat-list-item">{children}</li>,
            }}
        >
            {processedContent}
        </ReactMarkdown>
      </div>
    );
  };

  // --- STYLES: 100% JS Controlled Positioning ---
  const containerStyle = isExpanded 
    ? { 
        position: 'fixed',
        top: '50%', 
        left: '50%', 
        bottom: 'auto', 
        right: 'auto',
        transform: 'translate(-50%, -50%)', 
        width: '80vw', 
        height: '85vh', 
        maxWidth: '1000px',
        zIndex: 10000, 
        margin: 0,
        borderRadius: '12px'
      }
    : { 
        position: 'fixed',
        bottom: '90px', 
        right: '30px',
        top: 'auto',
        left: 'auto',
        transform: `translate(${position.x}px, ${position.y}px)`,
        width: '400px',
        height: '600px',
        zIndex: 9999,
        borderRadius: '16px'
      };

  return (
    <div 
        className={`modern-chat-wrapper ${isExpanded ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`} 
        style={containerStyle}
    >
      <div className="chat-header-modern" onMouseDown={handleMouseDown}>
        <div className="header-title">
            <img src={logo} alt="BoardWatch" className="header-logo" />
            <h3>BoardWatch AI</h3>
            <span className="beta-tag">BETA</span>
        </div>
        
        <div className="header-controls">
            <button 
                className="icon-btn" 
                onClick={() => { 
                    setIsExpanded(!isExpanded); 
                    if (!isExpanded) setPosition({x:0, y:0}); 
                }}
                title={isExpanded ? "Minimize" : "Expand"}
            >
                {isExpanded ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>
                ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                )}
            </button>
            {onClose && (
                <button className="icon-btn close-btn" onClick={onClose} title="Close Chat">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
            )}
        </div>
      </div>

      <div className="chat-window-modern" ref={chatWindowRef}>
        {messages.length === 0 && (
          // RENAMED CLASS: chat-empty-state
          <div className="chat-empty-state">
            <h2>Board Intelligence</h2>
            <p className="subtext">Ask about meetings, votes, or specific topics.</p>
            <div className="suggestion-grid">
              {[
                "Summarize the latest meeting", 
                "What topics are discussed most?", 
                "When was saftey discussed?"
              ].map((q, i) => (
                <button key={i} onClick={() => setInput(q)}>{q}</button>
              ))}
            </div>
          </div>
        )}
        
        {messages.map((m, i) => (
          <div key={i} className={`message-row ${m.role}`}>
            {m.role === 'bot' && <div className="avatar bot">BW</div>}
            <div className={`message-bubble ${m.role}`}>
              {renderMessageContent(m)}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message-row bot">
            <div className="avatar bot">BW</div>
            <div className="message-bubble bot loading-bubble">
              <span className="chat-typing-dot"></span><span className="chat-typing-dot"></span><span className="chat-typing-dot"></span>
            </div>
          </div>
        )}
      </div>

      {/* RENAMED CLASS: chat-input-area-modern */}
      <form onSubmit={handleSubmit} className="chat-input-area-modern">
        {/* RENAMED CLASS: chat-input-wrapper */}
        <div className="chat-input-wrapper">
            <input 
                type="text" 
                value={input} 
                onChange={(e) => setInput(e.target.value)} 
                placeholder="Ask a question..."
            />
            <button type="submit" disabled={isLoading || !input.trim()} className="send-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
      </form>
    </div>
  );
};

export default Chatbot;